import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { PortalDsButton, PortalDsField, PortalDsInput, PortalDsSelect, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { InsertionOrder } from 'src/redux/billing/billing.types';
import { useInsertionOrderForm } from '../../hooks/use-insertion-order-form';
import { IoBoldsignAutocomplete } from './io-boldsign-search';
import { IoBoldsignBudgetTable } from './io-boldsign-budget-table';
import { IoBoldsignPreviewModal } from './io-boldsign-preview-modal';

interface IoBoldsignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'create' | 'edit' | 'view';
  initialData?: InsertionOrder;
  onSuccess?: () => void;
}

export function IoBoldsignModal({
  open,
  onOpenChange,
  mode = 'create',
  initialData,
  onSuccess,
}: IoBoldsignModalProps) {
  const {
    formData,
    budgetData,
    setBudgetData,
    validationErrors,
    isPreviewModalOpen,
    setIsPreviewModalOpen,
    previewData,
    isGenerating,
    isReadOnly,
    isSaving,
    isSending,
    isLoadingPreview,
    handleInputChange,
    handleSave,
    handlePreviewAndSend,
    handleApproveAndSend,
    handleBackToEdit,
    handleViewDocument,
    getTitle,
    getSuggestions,
  } = useInsertionOrderForm({
    open,
    onOpenChange,
    mode,
    initialData,
    onSuccess,
    provider: 'boldsign',
  });

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
      >
        <DialogContent className={cn(portalDs.modal, 'max-h-[95vh] gap-0 overflow-y-auto p-0 sm:max-w-[900px]')}>
          <DialogHeader className={portalDs.modalHeader}>
            <DialogTitle className={portalDs.modalTitle}>
              {getTitle()}
            </DialogTitle>
          </DialogHeader>

          <div className={cn(portalDs.modalBody, 'space-y-5')}>
            <div className="flex flex-col gap-5 md:flex-row">
              <div className="space-y-3 md:w-1/4">
                <PortalDsField
                  label="Company Name"
                  htmlFor="company_name"
                  required
                  error={validationErrors.company_name}
                >
                  <IoBoldsignAutocomplete
                    id="company_name"
                    value={formData.company_name || ''}
                    onChange={(value) => handleInputChange('company_name', value)}
                    placeholder="Company name"
                    suggestions={getSuggestions('company_name')}
                    required
                    disabled={isReadOnly}
                    hasError={Boolean(validationErrors.company_name)}
                  />
                </PortalDsField>
                <PortalDsField
                  label="Advertiser Name"
                  htmlFor="advertiser_name"
                  required
                  error={validationErrors.advertiser_name}
                >
                  <IoBoldsignAutocomplete
                    id="advertiser_name"
                    value={formData.advertiser_name}
                    onChange={(value) => handleInputChange('advertiser_name', value)}
                    placeholder="Advertiser name"
                    suggestions={getSuggestions('advertiser_name')}
                    required
                    disabled={isReadOnly}
                    hasError={Boolean(validationErrors.advertiser_name)}
                  />
                </PortalDsField>
                <PortalDsField
                  label="Campaign Name"
                  htmlFor="campaign_name"
                  required
                  error={validationErrors.campaign_name}
                >
                  <IoBoldsignAutocomplete
                    id="campaign_name"
                    value={formData.campaign_name}
                    onChange={(value) => handleInputChange('campaign_name', value)}
                    placeholder="Campaign name"
                    suggestions={getSuggestions('campaign_name')}
                    required
                    disabled={isReadOnly}
                    hasError={Boolean(validationErrors.campaign_name)}
                  />
                </PortalDsField>
              </div>

              <div className="hidden w-px bg-[#EDEEF2] md:block" />

              <div className="flex-1 space-y-3 md:w-3/4">
                <div className="flex flex-col justify-between gap-4 md:flex-row">
                  <div className="flex flex-grow flex-col gap-3 md:max-w-[160px]">
                    <PortalDsField
                      label="Start Date"
                      htmlFor="start_date"
                      required
                      error={validationErrors.start_date}
                    >
                      <PortalDsInput
                        id="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        required
                        disabled={isReadOnly}
                        className={cn(validationErrors.start_date && portalDs.inputError)}
                      />
                    </PortalDsField>
                    <PortalDsField
                      label="End Date"
                      htmlFor="end_date"
                      required
                      error={validationErrors.end_date}
                    >
                      <PortalDsInput
                        id="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => handleInputChange('end_date', e.target.value)}
                        required
                        disabled={isReadOnly}
                        className={cn(validationErrors.end_date && portalDs.inputError)}
                      />
                    </PortalDsField>
                  </div>

                  <div className="hidden w-px bg-[#EDEEF2] md:block" />

                  <div className="flex flex-grow flex-col gap-3">
                    <PortalDsField
                      label="Client Name"
                      htmlFor="client_name"
                      required
                      error={validationErrors.client_name}
                    >
                      <IoBoldsignAutocomplete
                        id="client_name"
                        value={formData.client_name}
                        onChange={(value) => handleInputChange('client_name', value)}
                        placeholder="Client name"
                        suggestions={getSuggestions('client_name')}
                        required
                        disabled={isReadOnly}
                        hasError={Boolean(validationErrors.client_name)}
                      />
                    </PortalDsField>
                    <PortalDsField
                      label="Client Email"
                      htmlFor="client_email"
                      required
                      error={validationErrors.client_email}
                    >
                      <IoBoldsignAutocomplete
                        id="client_email"
                        type="email"
                        value={formData.client_email}
                        onChange={(value) => handleInputChange('client_email', value)}
                        placeholder="client@example.com"
                        suggestions={getSuggestions('client_email')}
                        required
                        disabled={isReadOnly}
                        hasError={Boolean(validationErrors.client_email)}
                      />
                    </PortalDsField>
                  </div>

                  <div className="hidden w-px bg-[#EDEEF2] md:block" />

                  <div className="flex flex-grow flex-col gap-3">
                    <PortalDsField
                      label="Internal Name"
                      htmlFor="agent_name"
                      required
                      error={validationErrors.agent_name}
                    >
                      <IoBoldsignAutocomplete
                        id="agent_name"
                        value={formData.agent_name}
                        onChange={(value) => handleInputChange('agent_name', value)}
                        placeholder="Agent name"
                        suggestions={getSuggestions('agent_name')}
                        required
                        disabled={isReadOnly}
                        hasError={Boolean(validationErrors.agent_name)}
                      />
                    </PortalDsField>
                    <PortalDsField
                      label="Internal Email"
                      htmlFor="agent_email"
                      required
                      error={validationErrors.agent_email}
                    >
                      <IoBoldsignAutocomplete
                        id="agent_email"
                        type="email"
                        value={formData.agent_email}
                        onChange={(value) => handleInputChange('agent_email', value)}
                        placeholder="agent@example.com"
                        suggestions={getSuggestions('agent_email')}
                        required
                        disabled={isReadOnly}
                        hasError={Boolean(validationErrors.agent_email)}
                      />
                    </PortalDsField>
                  </div>
                </div>

                <PortalDsField
                  label="CC Emails (comma-separated)"
                  htmlFor="cc_emails"
                  hint="Include everyone who should receive status updates"
                  error={validationErrors.cc_emails}
                >
                  <PortalDsInput
                    id="cc_emails"
                    value={formData.cc_emails}
                    onChange={(e) => handleInputChange('cc_emails', e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                    disabled={isReadOnly}
                    className={cn(validationErrors.cc_emails && portalDs.inputError)}
                  />
                </PortalDsField>

                <div className="flex flex-wrap items-center gap-4">
                  <span className={portalDs.label}>
                    Additional Options
                  </span>
                  <PortalDsSelect
                    id="logo"
                    value={formData.logo}
                    onChange={(e) => handleInputChange('logo', e.target.value)}
                    disabled={isReadOnly}
                    className="w-auto min-w-[180px]"
                  >
                    <option value="invisibly">
                      Invisibly
                    </option>
                    <option value="campaign_insights">
                      Campaign Insights
                    </option>
                  </PortalDsSelect>
                  <label className={portalDs.formCheck}>
                    <input
                      type="checkbox"
                      id="prebill"
                      checked={formData.prebill}
                      onChange={(e) => handleInputChange('prebill', e.target.checked)}
                      disabled={isReadOnly}
                      className={portalDs.formCheckInput}
                    />
                    <span className={portalDs.formCheckLabel}>
                      Prebill
                    </span>
                  </label>
                  <label className={portalDs.formCheck}>
                    <input
                      type="checkbox"
                      id="include_sla"
                      checked={formData.include_sla}
                      onChange={(e) => handleInputChange('include_sla', e.target.checked)}
                      disabled={isReadOnly}
                      className={portalDs.formCheckInput}
                    />
                    <span className={portalDs.formCheckLabel}>
                      Include SLA
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <IoBoldsignBudgetTable
              initialData={budgetData}
              onChange={setBudgetData}
              disabled={isReadOnly}
            />
          </div>

          <DialogFooter className={cn(portalDs.modalFooter, 'sm:space-x-0')}>
            <PortalDsButton
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </PortalDsButton>
            {isReadOnly && initialData?.document_url ? (
              <PortalDsButton
                variant="accent"
                onClick={handleViewDocument}
              >
                View Document
              </PortalDsButton>
            ) : null}
            {!isReadOnly ? (
              <>
                <PortalDsButton
                  variant="secondary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </PortalDsButton>
                <PortalDsButton
                  variant="accent"
                  onClick={handlePreviewAndSend}
                  disabled={isSaving || isLoadingPreview || isGenerating}
                >
                  {(isSaving || isLoadingPreview || isGenerating) ? 'Generating...' : 'Preview & Send'}
                </PortalDsButton>
              </>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <IoBoldsignPreviewModal
        open={isPreviewModalOpen}
        onOpenChange={setIsPreviewModalOpen}
        documentUrl={previewData?.document_url || null}
        onApproveAndSend={handleApproveAndSend}
        onBackToEdit={handleBackToEdit}
        isLoading={isSending}
        viewOnly={isReadOnly}
      />
    </>
  );
}
